<template>
  <div class="design-detail" v-loading="loading">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <el-button @click="$router.push('/design')" :icon="ArrowLeft" circle />
        <h2>{{ design?.main_style_number || design?.style_number || '디자인 상세' }}</h2>
        <span v-if="design?.main_style_number && design?.style_number" class="internal-style-no">{{ design.style_number }}</span>
        <el-button size="small" @click="helpVisible = true" style="margin-left:8px">도움말</el-button>
        <HelpPanel v-model="helpVisible" :content-path="'screens/design-detail'" />
        <el-tag v-if="design" size="large">{{ design.sample_status_display || design.sample_status }}</el-tag>
        <el-tag v-if="design?.is_locked" type="warning" size="large">잠금</el-tag>
      </div>
      <div class="header-actions">
        <el-button @click="showCopyDialog = true" :icon="CopyDocument">복사</el-button>
        <el-button @click="openEditDialog" :icon="Edit" type="primary">수정</el-button>
        <el-button @click="fetchOverview" :icon="Refresh">새로고침</el-button>
      </div>
    </div>

    <template v-if="design">
      <!-- 요약 영역: 이미지 + 정보 카드 -->
      <el-row :gutter="20" class="summary-row">
        <!-- 디자인 이미지 (정면/후면) -->
        <el-col :span="design.front_image_url ? 5 : 0" v-if="design.front_image_url">
          <el-card shadow="hover" class="design-image-card">
            <div class="design-images">
              <el-image
                :src="design.front_image_url"
                fit="contain"
                class="design-main-image"
                :preview-src-list="imageList"
              />
              <el-image
                v-if="design.back_image_url"
                :src="design.back_image_url"
                fit="contain"
                class="design-sub-image"
                :preview-src-list="imageList"
                :initial-index="1"
              />
            </div>
          </el-card>
        </el-col>

        <!-- 정보 카드 -->
        <el-col :span="design.front_image_url ? 19 : 24">
          <el-row :gutter="12">
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">브랜드</div>
                <div class="summary-value">{{ design.brand_name || '-' }}</div>
              </el-card>
            </el-col>
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">담당MD</div>
                <div class="summary-value">{{ design.designer_name || '-' }}</div>
              </el-card>
            </el-col>
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">시즌</div>
                <div class="summary-value">{{ design.year || '' }} {{ design.season_display || '' }}</div>
              </el-card>
            </el-col>
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">구분</div>
                <div class="summary-value">{{ design.sample_main_type_display || '-' }}</div>
              </el-card>
            </el-col>
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">요청일</div>
                <div class="summary-value">{{ design.request_date || '-' }}</div>
              </el-card>
            </el-col>
            <el-col :span="4">
              <el-card shadow="hover" class="summary-card">
                <div class="summary-label">품명</div>
                <div class="summary-value">{{ design.product_name || '-' }}</div>
              </el-card>
            </el-col>
          </el-row>
        </el-col>
      </el-row>

      <!-- 라이프사이클 파이프라인 -->
      <LifecyclePipeline
        :lifecycle="lifecycle"
        :material-order-count="materialOrders.length"
        :trade-data-exists="lcs.length > 0 || shipments.length > 0"
        @select-tab="activeTab = $event"
      />

      <!-- 탭 -->
      <el-tabs v-model="activeTab">
        <!-- 기본정보 탭 -->
        <el-tab-pane label="기본정보" name="info">
          <el-card>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="내부번호">{{ design.style_number || '-' }}</el-descriptions-item>
              <el-descriptions-item label="고객사 스타일번호">{{ design.main_style_number || '-' }}</el-descriptions-item>
              <el-descriptions-item label="품명">{{ design.product_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="브랜드">{{ design.brand_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="담당MD">{{ design.designer_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="시즌">{{ design.year || '' }} {{ design.season_display || '' }}</el-descriptions-item>
              <el-descriptions-item label="원본 디자인">
                <router-link
                  v-if="design.parent_design"
                  :to="`/design/${design.parent_design}`"
                  style="color: #409EFF; text-decoration: none;"
                >{{ design.parent_style_number }}</router-link>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="파생 디자인">
                <span v-if="design.variant_count > 0">{{ design.variant_count }}건</span>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="구분">{{ design.sample_main_type_display || '-' }}</el-descriptions-item>
              <el-descriptions-item label="성별">{{ design.gender_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="상품유형">{{ design.product_type_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="스타일">{{ design.style_name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="난이도">{{ design.difficulty_display || '-' }}</el-descriptions-item>
              <el-descriptions-item label="세트여부">{{ design.is_set ? '예' : '아니오' }}</el-descriptions-item>
              <el-descriptions-item label="리버시블">{{ design.is_reversible ? '예' : '아니오' }}</el-descriptions-item>
              <el-descriptions-item label="안감">{{ design.liner_type_display || '-' }}</el-descriptions-item>
              <el-descriptions-item label="요청일">{{ design.request_date || '-' }}</el-descriptions-item>
              <el-descriptions-item label="메모" :span="2">{{ design.memo || '-' }}</el-descriptions-item>
            </el-descriptions>

            <!-- 혼용율 -->
            <div v-if="design.mix_ratios && design.mix_ratios.length > 0" style="margin-top: 15px;">
              <h4>혼용율</h4>
              <el-table :data="design.mix_ratios" border size="small" style="max-width: 400px;">
                <el-table-column prop="material_name" label="소재" />
                <el-table-column prop="ratio_percent" label="비율(%)" width="100" align="right" />
              </el-table>
            </div>
          </el-card>
        </el-tab-pane>

        <!-- 마카 탭 (디자인 속성) -->
        <el-tab-pane name="markers">
          <template #label>
            <span class="tab-spec">마카</span> <el-badge v-if="markers.length" :value="markers.length" type="info" class="tab-badge" />
          </template>
          <MarkerTab
            :design-id="design.id"
            :markers="markers"
            :can-edit="!design.is_locked"
            @updated="fetchOverview"
          />
        </el-tab-pane>

        <!-- 요척 탭 -->
        <el-tab-pane name="yardages">
          <template #label>
            <span class="tab-spec">요척</span> <el-badge v-if="yardages.length" :value="yardages.length" type="info" class="tab-badge" />
          </template>
          <YardageTab
            :design-id="design.id"
            :yardages="yardages"
            :markers="markers"
            :can-edit="!design.is_locked"
            @updated="fetchOverview"
          />
        </el-tab-pane>

        <!-- 다운스펙 탭 -->
        <el-tab-pane name="downSpec">
          <template #label>
            <span class="tab-spec">다운스펙</span> <el-badge v-if="downSpecs.length" :value="downSpecs.length" type="info" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <el-button type="primary" size="small" @click="showDownSpecDialog = true">다운스펙 추가</el-button>
          </div>
          <el-table
            :data="downSpecs"
            border
            size="small"
            stripe
            row-key="id"
          >
            <el-table-column type="expand">
              <template #default="{ row }">
                <div style="padding: 8px 20px;">
                  <h4 style="margin: 0 0 8px; color: #606266;">부위별 상세</h4>
                  <el-table :data="row.parts || []" border size="small" style="max-width: 700px;">
                    <el-table-column prop="part_name" label="부위" width="120" />
                    <el-table-column label="개당중량(g)" width="120" align="right">
                      <template #default="{ row: part }">{{ Number(part.weight_per_piece).toFixed(1) }}</template>
                    </el-table-column>
                    <el-table-column prop="piece_count" label="패턴개수" width="100" align="center" />
                    <el-table-column label="소계(g)" width="120" align="right">
                      <template #default="{ row: part }">{{ Number(part.total_weight).toFixed(1) }}</template>
                    </el-table-column>
                  </el-table>
                  <el-empty v-if="!row.parts || row.parts.length === 0" description="부위 상세가 없습니다." :image-size="40" />
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="size" label="사이즈" width="100" />
            <el-table-column label="총중량(g)" width="120" align="right">
              <template #default="{ row }">{{ Number(row.total_weight).toFixed(1) }}</template>
            </el-table-column>
            <el-table-column prop="down_type" label="다운종류" width="120" />
            <el-table-column prop="down_ratio" label="충전비율" width="120" />
            <el-table-column prop="measurement_date" label="측정일" width="120" />
            <el-table-column prop="remarks" label="비고" min-width="150" />
            <el-table-column label="" width="70" align="center">
              <template #default="{ row }">
                <el-button type="danger" size="small" text @click.stop="deleteDownSpec(row.id)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="downSpecs.length === 0" description="등록된 다운스펙이 없습니다." />
        </el-tab-pane>

        <!-- 진행(트랜잭션) 탭: 샘플/견적/생산/자재발주/무역 — 디자인 속성 탭 뒤로 (2026-07-19) -->
        <el-tab-pane name="samples">
          <template #label>
            샘플 <el-badge v-if="samples.length" :value="samples.length" type="primary" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <el-button type="primary" size="small" @click="showSampleDialog = true">샘플요청 추가</el-button>
          </div>
          <el-table
            :data="samples"
            border
            size="small"
            stripe
            @row-click="row => $router.push(`/sample-requests/${row.id}`)"
            style="cursor: pointer;"
          >
            <el-table-column prop="request_number" label="요청번호" width="140" />
            <el-table-column prop="sample_type_display" label="유형" width="100" />
            <el-table-column label="상태" width="100">
              <template #default="{ row }">
                <el-tag :type="getSampleStatusType(row.status)" size="small">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="brand_name" label="브랜드" width="100" />
            <el-table-column prop="due_date" label="납기일" width="110" />
            <el-table-column label="D-day" width="80" align="center">
              <template #default="{ row }">
                <el-tag
                  v-if="row.days_remaining != null && !['COMPLETED', 'REJECTED'].includes(row.status)"
                  :type="row.is_overdue ? 'danger' : row.days_remaining <= 3 ? 'warning' : 'info'"
                  size="small"
                >
                  {{ row.is_overdue ? 'D+' + Math.abs(row.days_remaining) : 'D-' + row.days_remaining }}
                </el-tag>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="request_date" label="요청일" width="110" />
          </el-table>
          <el-empty v-if="samples.length === 0" description="샘플요청이 없습니다." />
        </el-tab-pane>

        <el-tab-pane name="quotations">
          <template #label>
            견적 <el-badge v-if="quotations.length" :value="quotations.length" type="primary" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <el-button v-if="userStore.hasQuotationAccess" type="primary" size="small" @click="$router.push(`/quotations/new?design=${design.id}`)">견적서 작성</el-button>
          </div>
          <el-table
            :data="quotations"
            border
            size="small"
            stripe
            @row-click="navigateQuotation"
            style="cursor: pointer;"
          >
            <el-table-column prop="quotation_number" label="견적번호" width="140" />
            <el-table-column label="버전" width="60" align="center">
              <template #default="{ row }">
                <el-tag :type="row.version === 'v2' ? '' : 'info'" size="small">{{ row.version }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="유형" width="80">
              <template #default="{ row }">
                {{ row.quotation_type_display || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="상태" width="90">
              <template #default="{ row }">
                <el-tag :type="getQuotationStatusType(row.status)" size="small">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="발주수량" width="100" align="right">
              <template #default="{ row }">{{ (row.order_quantity || 0).toLocaleString() }}</template>
            </el-table-column>
            <el-table-column v-if="userStore.hasQuotationAccess" label="견적가" width="120" align="right">
              <template #default="{ row }">{{ formatCurrency(row.final_price) }}</template>
            </el-table-column>
            <el-table-column v-if="userStore.hasQuotationAccess" label="PCS단가" width="100" align="right">
              <template #default="{ row }">{{ row.unit_price ? formatCurrency(row.unit_price) : '-' }}</template>
            </el-table-column>
            <el-table-column v-if="userStore.hasQuotationAccess" label="마진%" width="80" align="right">
              <template #default="{ row }">
                {{ row.margin_percentage != null ? row.margin_percentage + '%' : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="request_date" label="요청일" width="110" />
          </el-table>
          <el-empty v-if="quotations.length === 0" description="견적서가 없습니다." />
        </el-tab-pane>

        <el-tab-pane name="production">
          <template #label>
            생산 <el-badge v-if="mainRequests.length" :value="mainRequests.length" type="primary" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <el-button type="primary" size="small" @click="showMainRequestDialog = true">메인오더 추가</el-button>
          </div>
          <el-table
            :data="mainRequests"
            border
            size="small"
            stripe
            @row-click="row => $router.push(`/main-requests/${row.id}`)"
            style="cursor: pointer;"
          >
            <el-table-column prop="request_number" label="요청번호" width="140" />
            <el-table-column label="상태" width="100">
              <template #default="{ row }">
                <el-tag :type="getProductionStatusType(row.status)" size="small">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="manufacturer_name" label="공장" width="120" />
            <el-table-column label="발주수량" width="100" align="right">
              <template #default="{ row }">{{ (row.order_quantity || 0).toLocaleString() }}</template>
            </el-table-column>
            <el-table-column prop="delivery_date" label="납기일" width="110" />
            <el-table-column label="D-day" width="80" align="center">
              <template #default="{ row }">
                <el-tag
                  v-if="row.days_until_delivery != null && !['DELIVERED','COMPLETED','CANCELLED'].includes(row.status)"
                  :type="row.is_delayed ? 'danger' : row.days_until_delivery <= 7 ? 'warning' : 'info'"
                  size="small"
                >
                  {{ row.is_delayed ? 'D+' + Math.abs(row.days_until_delivery) : 'D-' + row.days_until_delivery }}
                </el-tag>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="마일스톤" width="150">
              <template #default="{ row }">
                <div v-if="row.milestone_progress && row.milestone_progress.total > 0" style="display: flex; align-items: center; gap: 8px;">
                  <el-progress
                    :percentage="row.milestone_progress.percent"
                    :stroke-width="14"
                    :show-text="false"
                    style="flex: 1;"
                  />
                  <span style="font-size: 12px; color: #606266; white-space: nowrap;">
                    {{ row.milestone_progress.completed }}/{{ row.milestone_progress.total }}
                  </span>
                </div>
                <span v-else style="color: #909399;">-</span>
              </template>
            </el-table-column>
            <el-table-column v-if="canViewCosts" label="원가" width="120" align="right">
              <template #default="{ row }">
                {{ row.total_cost && Number(row.total_cost) > 0 ? formatCurrency(row.total_cost) : '-' }}
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="mainRequests.length === 0" description="메인오더가 없습니다." />
        </el-tab-pane>

        <el-tab-pane name="materials">
          <template #label>
            자재발주 <el-badge v-if="materialOrders.length" :value="materialOrders.length" type="primary" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <el-button type="primary" size="small" @click="$router.push(`/material-orders/new?design=${design.id}`)">자재발주 추가</el-button>
          </div>
          <el-table
            :data="materialOrders"
            border
            size="small"
            stripe
            @row-click="row => $router.push(`/material-orders/${row.id}`)"
            style="cursor: pointer;"
          >
            <el-table-column prop="material_type_display" label="유형" width="100" />
            <el-table-column prop="supplier_name" label="공급업체" width="150" />
            <el-table-column prop="order_date" label="발주일" width="110" />
            <el-table-column prop="delivery_date" label="납기일" width="110" />
            <el-table-column label="수량" width="100" align="right">
              <template #default="{ row }">{{ (row.request_quantity || 0).toLocaleString() }}</template>
            </el-table-column>
          </el-table>
          <el-empty v-if="materialOrders.length === 0" description="자재발주가 없습니다." />
        </el-tab-pane>

        <el-tab-pane name="trade">
          <template #label>
            무역/선적 <el-badge v-if="lcs.length + shipments.length > 0" :value="lcs.length + shipments.length" type="primary" class="tab-badge" />
          </template>
          <div class="tab-action-bar">
            <span></span>
            <div style="display: flex; gap: 8px;">
              <el-button type="primary" size="small" @click="showLCDialog = true">LC 추가</el-button>
              <el-button type="primary" size="small" @click="showShipmentDialog = true">선적 추가</el-button>
            </div>
          </div>
          <el-row :gutter="20">
            <el-col :span="12">
              <h4 style="margin-bottom: 10px; color: #303133;">LC 현황</h4>
              <el-table :data="lcs" border size="small" stripe @row-click="row => $router.push(`/lcs/${row.id}`)">
                <el-table-column prop="lc_number" label="LC번호" width="130" />
                <el-table-column label="상태" width="80">
                  <template #default="{ row }">
                    <el-tag :type="getLCStatusType(row.status)" size="small">{{ row.status_display }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="금액" align="right">
                  <template #default="{ row }">{{ row.currency }} {{ formatNumber(row.amount) }}</template>
                </el-table-column>
                <el-table-column prop="expiry_date" label="유효기일" width="100" />
              </el-table>
              <el-empty v-if="lcs.length === 0" description="연결된 LC가 없습니다" :image-size="60" />
            </el-col>
            <el-col :span="12">
              <h4 style="margin-bottom: 10px; color: #303133;">선적 스케줄</h4>
              <el-table :data="shipments" border size="small" stripe>
                <el-table-column prop="shipment_number" label="선적번호" width="120" />
                <el-table-column label="상태" width="80">
                  <template #default="{ row }">
                    <el-tag :type="getShipmentStatusType(row.status)" size="small">{{ row.status_display }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="quantity" label="수량" width="80" align="right" />
                <el-table-column prop="etd" label="ETD" width="100" />
                <el-table-column prop="eta" label="ETA" width="100" />
              </el-table>
              <el-empty v-if="shipments.length === 0" description="등록된 선적이 없습니다" :image-size="60" />
            </el-col>
          </el-row>
        </el-tab-pane>

        <!-- 원가 탭 (권한 있을 때만) -->
        <el-tab-pane v-if="costSummary" label="원가" name="cost">
          <el-card>
            <!-- 총원가 = 개발비(샘플·패턴비 원장) + 메인(작업지시) 원가 (2026-10-18 UAT P5) -->
            <el-row :gutter="20">
              <el-col :span="6">
                <el-statistic title="개발비 (샘플·패턴)" :value="costSummary.development_cost || 0" :precision="0">
                  <template #suffix>원</template>
                </el-statistic>
              </el-col>
              <el-col :span="6">
                <el-statistic title="메인 원가" :value="costSummary.main_cost ?? costSummary.total_cost" :precision="0">
                  <template #suffix>원</template>
                </el-statistic>
              </el-col>
              <el-col :span="6">
                <el-statistic title="총원가 (개발비 포함)" :value="costSummary.total_cost" :precision="0">
                  <template #suffix>원</template>
                </el-statistic>
              </el-col>
              <el-col :span="6">
                <el-statistic title="메인 PCS단가" :value="costSummary.unit_cost" :precision="2">
                  <template #suffix>원</template>
                </el-statistic>
              </el-col>
            </el-row>
            <div style="margin-top:6px;color:#909399;font-size:12px">
              총수량 {{ Number(costSummary.total_quantity || 0).toLocaleString() }} EA · 개발비는 수량과 무관한 고정비라 PCS단가에는 넣지 않습니다.
            </div>
          </el-card>
        </el-tab-pane>
      </el-tabs>
    </template>

    <!-- 수정 다이얼로그 -->
    <DesignFormModal ref="designFormModal" @saved="fetchOverview" />

    <!-- 액션 다이얼로그 -->
    <SampleRequestFormDialog
      v-model="showSampleDialog"
      :default-data="designDefaultData"
      @saved="fetchOverview"
    />
    <MainRequestFormDialog
      v-model="showMainRequestDialog"
      :default-data="designDefaultData"
      @saved="fetchOverview"
    />
    <LCFormDialog
      v-model="showLCDialog"
      :default-data="tradeDefaultData"
      @saved="fetchOverview"
    />
    <ShipmentFormDialog
      v-model="showShipmentDialog"
      :default-data="tradeDefaultData"
      @saved="fetchOverview"
    />

    <!-- 디자인 복사 다이얼로그 -->
    <el-dialog v-model="showCopyDialog" title="디자인 복사" width="420px">
      <el-form label-width="80px">
        <el-form-item label="원본">
          <el-input :model-value="design?.style_number" disabled />
        </el-form-item>
        <el-form-item label="브랜드">
          <el-select v-model="copyBrand" filterable clearable placeholder="원본과 동일" style="width: 100%">
            <el-option v-for="b in codeStore.brands" :key="b.id" :label="b.name" :value="b.id" />
          </el-select>
        </el-form-item>
        <p style="color: #909399; font-size: 13px; margin: 0;">
          브랜드를 선택하지 않으면 원본과 동일한 브랜드로 복사됩니다.
        </p>
      </el-form>
      <template #footer>
        <el-button @click="showCopyDialog = false">취소</el-button>
        <el-button type="primary" @click="executeDuplicate" :loading="copyLoading">복사</el-button>
      </template>
    </el-dialog>

    <!-- 다운스펙 추가 다이얼로그 -->
    <el-dialog v-model="showDownSpecDialog" title="다운스펙 추가" width="680px" :close-on-click-modal="false" destroy-on-close>
      <el-form :model="downSpecForm" label-width="100px" ref="downSpecFormRef">
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="사이즈" prop="size" :rules="[{required:true, message:'필수'}]">
              <el-input v-model="downSpecForm.size" placeholder="예: 55, 66, FREE" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="다운종류" prop="down_type" :rules="[{required:true, message:'필수'}]">
              <el-input v-model="downSpecForm.down_type" placeholder="예: 구스다운" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="충전비율">
              <el-input v-model="downSpecForm.down_ratio" placeholder="예: 90:10" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="총중량(g)" prop="total_weight" :rules="[{required:true, message:'필수'}]">
              <el-input-number v-model="downSpecForm.total_weight" :min="0" :precision="1" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="측정일">
              <el-date-picker v-model="downSpecForm.measurement_date" value-format="YYYY-MM-DD" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="비고">
              <el-input v-model="downSpecForm.remarks" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-divider content-position="left">부위별 상세</el-divider>
        <el-table :data="downSpecForm.parts" border size="small" style="margin-bottom: 8px;">
          <el-table-column label="부위명" min-width="120">
            <template #default="{ row }">
              <el-input v-model="row.part_name" size="small" placeholder="예: 앞판" />
            </template>
          </el-table-column>
          <el-table-column label="개당중량(g)" width="130">
            <template #default="{ row }">
              <el-input-number v-model="row.weight_per_piece" :min="0" :precision="1" size="small" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="패턴개수" width="100">
            <template #default="{ row }">
              <el-input-number v-model="row.piece_count" :min="1" size="small" style="width:100%" />
            </template>
          </el-table-column>
          <el-table-column label="" width="60" align="center">
            <template #default="{ $index }">
              <el-button type="danger" size="small" text @click="downSpecForm.parts.splice($index, 1)">삭제</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-button size="small" @click="downSpecForm.parts.push({ part_name: '', weight_per_piece: 0, piece_count: 1 })">
          + 부위 추가
        </el-button>
      </el-form>
      <template #footer>
        <el-button @click="showDownSpecDialog = false">취소</el-button>
        <el-button type="primary" @click="saveDownSpec" :loading="downSpecSaving">저장</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, Refresh, Edit, CopyDocument } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import { useUserStore } from '@/stores/user'
import LifecyclePipeline from '@/components/design/LifecyclePipeline.vue'
import MarkerTab from '@/components/design/MarkerTab.vue'
import YardageTab from '@/components/design/YardageTab.vue'
import DesignFormModal from '@/components/DesignFormModal.vue'
import SampleRequestFormDialog from '@/components/sample/SampleRequestFormDialog.vue'
import MainRequestFormDialog from '@/components/production/MainRequestFormDialog.vue'
import LCFormDialog from '@/components/trade/LCFormDialog.vue'
import ShipmentFormDialog from '@/components/trade/ShipmentFormDialog.vue'

const route = useRoute()
const router = useRouter()
const codeStore = useCodeStore()
const userStore = useUserStore()

// 원가/마진 필드 조회 권한
const canViewCosts = computed(() => userStore.hasQuotationAccess || userStore.hasCostAccess)

const loading = ref(false)
const activeTab = ref('info')
const designFormModal = ref(null)

// 다이얼로그 표시
const showCopyDialog = ref(false)
const copyBrand = ref(null)
const copyLoading = ref(false)
const showSampleDialog = ref(false)
const showMainRequestDialog = ref(false)
const showLCDialog = ref(false)
const showShipmentDialog = ref(false)

// Overview 데이터
const design = ref(null)
const lifecycle = ref({})
const samples = ref([])
const quotations = ref([])
const mainRequests = ref([])
const materialOrders = ref([])
const costSummary = ref(null)
const markers = ref([])
const yardages = ref([])

// 무역 데이터 (별도 조회)
const lcs = ref([])
const shipments = ref([])

// 다운스펙 데이터
const downSpecs = ref([])
const showDownSpecDialog = ref(false)
const downSpecSaving = ref(false)
const downSpecFormRef = ref(null)
const downSpecForm = ref({
  size: '',
  total_weight: 0,
  down_type: '',
  down_ratio: '',
  measurement_date: '',
  remarks: '',
  parts: [],
})

const imageList = computed(() => {
  const list = []
  if (design.value?.front_image_url) list.push(design.value.front_image_url)
  if (design.value?.back_image_url) list.push(design.value.back_image_url)
  return list
})

const designDefaultData = computed(() => ({
  brand: design.value?.brand,
  design: design.value?.id,
  order_quantity: design.value?.order_quantity,
  delivery_date: design.value?.due_date,
}))

const tradeDefaultData = computed(() => {
  const firstMR = mainRequests.value.length === 1 ? mainRequests.value[0].id : null
  return { main_request: firstMR }
})

const fetchOverview = async () => {
  loading.value = true
  try {
    const res = await axiosInstance.get(`designs/${route.params.id}/overview/`)
    const data = res.data

    design.value = data.design
    lifecycle.value = data.lifecycle || {}
    samples.value = data.samples || []
    quotations.value = data.quotations || []
    mainRequests.value = data.main_requests || []
    materialOrders.value = data.material_orders || []
    costSummary.value = data.cost_summary || null
    markers.value = data.markers || []
    yardages.value = data.yardages || []

    // 무역 데이터: main_request ID 기반으로 LC/선적 조회
    await fetchTradeData()

    // 다운스펙 조회
    await fetchDownSpecs()
  } catch (error) {
    console.error('Failed to fetch design overview:', error)
  } finally {
    loading.value = false
  }
}

const fetchTradeData = async () => {
  if (mainRequests.value.length === 0) {
    lcs.value = []
    shipments.value = []
    return
  }

  try {
    const promises = []
    for (const mr of mainRequests.value) {
      promises.push(
        axiosInstance.get('lcs/', { params: { main_request: mr.id } }).catch(() => ({ data: [] })),
        axiosInstance.get('shipments/', { params: { main_request: mr.id } }).catch(() => ({ data: [] }))
      )
    }
    const results = await Promise.all(promises)

    const allLCs = []
    const allShipments = []
    for (let i = 0; i < results.length; i += 2) {
      const lcData = results[i].data.results || results[i].data || []
      const shipData = results[i + 1].data.results || results[i + 1].data || []
      allLCs.push(...lcData)
      allShipments.push(...shipData)
    }

    lcs.value = allLCs
    shipments.value = allShipments
  } catch (error) {
    console.error('Failed to fetch trade data:', error)
  }
}

// 수정 다이얼로그 열기
const openEditDialog = () => {
  if (design.value) {
    designFormModal.value?.open(design.value)
  }
}

// 디자인 복사 실행
const executeDuplicate = async () => {
  copyLoading.value = true
  try {
    const payload = {}
    if (copyBrand.value) {
      payload.brand = copyBrand.value
    }
    const res = await axiosInstance.post(`designs/${design.value.id}/duplicate/`, payload)
    ElMessage.success(`복사 완료: ${res.data.style_number}`)
    showCopyDialog.value = false
    copyBrand.value = null
    router.push(`/design/${res.data.id}`)
  } catch (err) {
    console.error('디자인 복사 실패:', err)
    ElMessage.error('디자인 복사 중 오류가 발생했습니다')
  } finally {
    copyLoading.value = false
  }
}

// 다운스펙 조회
const fetchDownSpecs = async () => {
  if (!design.value?.id) return
  try {
    const res = await axiosInstance.get(`designs/${design.value.id}/down-specifications/`)
    downSpecs.value = res.data || []
  } catch (error) {
    console.error('다운스펙 조회 실패:', error)
    downSpecs.value = []
  }
}

// 다운스펙 저장
const saveDownSpec = async () => {
  if (downSpecFormRef.value) {
    try {
      await downSpecFormRef.value.validate()
    } catch { return }
  }
  downSpecSaving.value = true
  try {
    const payload = {
      ...downSpecForm.value,
      parts: downSpecForm.value.parts.filter(p => p.part_name),
    }
    await axiosInstance.post(`designs/${design.value.id}/down-specifications/`, payload)
    ElMessage.success('다운스펙이 등록되었습니다')
    showDownSpecDialog.value = false
    downSpecForm.value = { size: '', total_weight: 0, down_type: '', down_ratio: '', measurement_date: '', remarks: '', parts: [] }
    await fetchDownSpecs()
  } catch (error) {
    console.error('다운스펙 저장 실패:', error)
    ElMessage.error('다운스펙 저장 중 오류가 발생했습니다')
  } finally {
    downSpecSaving.value = false
  }
}

// 다운스펙 삭제
const deleteDownSpec = async (specId) => {
  try {
    await axiosInstance.delete(`designs/${design.value.id}/down-specifications/${specId}/`)
    ElMessage.success('삭제되었습니다')
    await fetchDownSpecs()
  } catch (error) {
    console.error('다운스펙 삭제 실패:', error)
    ElMessage.error('삭제 중 오류가 발생했습니다')
  }
}

// 견적 행 클릭 네비게이션 (v1 vs v2)
const navigateQuotation = (row) => {
  if (row.version === 'v2') {
    router.push(`/quotations/${row.id}`)
  } else {
    router.push(`/costEstimation?id=${row.id}`)
  }
}

// 포맷 함수
const formatCurrency = (amount) => {
  if (amount == null) return '-'
  return Number(amount).toLocaleString() + '원'
}

const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString()
}

// 상태 타입 매핑
const getSampleStatusType = (status) => {
  const map = { REQUESTED: 'info', DEPT_APPROVED: 'warning', FINAL_APPROVED: 'warning', IN_PROGRESS: '', COMPLETED: 'success', REJECTED: 'danger' }
  return map[status] || 'info'
}

const getQuotationStatusType = (status) => {
  const map = {
    DRAFT: 'info', MATERIAL_DONE: '', CMT_DONE: '', EXPENSE_DONE: '', COMPLETED: 'success', CONFIRMED: 'success',
    submitted: 'warning', received: 'warning', processing: '', completed: 'success'
  }
  return map[status] || 'info'
}

const getProductionStatusType = (status) => {
  const map = {
    REQUESTED: 'info', MATERIAL_ORDERING: 'warning', IN_PRODUCTION: '', SHIPPING: '', DELIVERED: 'success', COMPLETED: 'success', CANCELLED: 'danger'
  }
  return map[status] || 'info'
}

const getLCStatusType = (status) => {
  const map = { DRAFT: 'info', REQUESTED: 'warning', ISSUED: '', SHIPPED: 'success', SETTLED: 'info' }
  return map[status] || 'info'
}

const getShipmentStatusType = (status) => {
  const map = { PLANNED: 'info', BOOKED: 'warning', LOADED: '', IN_TRANSIT: '', ARRIVED: 'success', DELIVERED: 'success' }
  return map[status] || 'info'
}

onMounted(() => {
  codeStore.fetchBrands()
  fetchOverview()
})
</script>

<style scoped>
.design-detail {
  padding: 20px;
}

/* 설계 자료(전문 입력) 탭 — 진행 탭과 시각 구분 (회색 톤) */
.tab-spec { color: #909399; }

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left h2 {
  margin: 0;
}

.internal-style-no {
  font-size: var(--sw-font-sm);
  color: var(--el-text-color-secondary);
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* 요약 카드 */
.summary-row {
  margin-bottom: 16px;
}

.summary-card {
  text-align: center;
  height: 100%;
}

.summary-card :deep(.el-card__body) {
  padding: 12px 8px;
}

/* 디자인 이미지 카드 */
.design-image-card :deep(.el-card__body) {
  padding: 8px;
}

.design-images {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.design-main-image {
  width: 100%;
  max-height: 160px;
  cursor: pointer;
}

.design-sub-image {
  width: 60px;
  height: 60px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 탭 액션 바 */
.tab-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

/* 탭 뱃지 */
.tab-badge {
  margin-left: 4px;
}

.tab-badge :deep(.el-badge__content) {
  font-size: 10px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
}
</style>
